<template>
  <div class="componentRoot" v-if="timeTable" @keyup.esc="hideDuplicateTimeSlot">
    <v-container fluid v-if="duplicationMode && activeTimeSlot && !profileView">
      <v-row>
        <v-col class="pb-0">
          {{ $t("messages.addFollowUpFor") }}
        </v-col>
      </v-row>
      <v-row>
        <v-col :cols="followUpCols" class="pt-2">
          <TimeTableCard :item="{ rendered: true, value: activeTimeSlot }" :duplicationMode="true" :heightPerSlot="60"
            @click="hideDuplicateTimeSlot" />
        </v-col>
      </v-row>
    </v-container>
    <v-card class="pa-0 mb-2" flat>
      <v-container fluid class="pa-2">
        <v-row>
          <v-col class="pa-0" cols="auto" style="display: flex; justify-content: space-between; align-items: center">
            <v-menu v-model="datePicker" :close-on-content-click="false" transition="v-scale-transition"
              min-width="auto" location="center" position="absolute">
              <template v-slot:activator="{ props }">
                <h3 style="display: flex; justify-content: space-between; align-items: center">
                  <v-icon @click="fetchTimeTable(-maxDays)"> mdi-chevron-left </v-icon>
                  <span v-bind="props">{{ dateString }}</span>
                  <v-icon @click="fetchTimeTable(maxDays)"> mdi-chevron-right </v-icon>
                </h3>
              </template>

              <v-date-picker v-model="pickerDate" v-if="datePicker" color="rgb(var(--v-theme-primary))"
                :show-week="true" hide-header :show-adjacent-months="true" @update:modelValue="
                  datePicker = false;
                selectDate();
                ">
              </v-date-picker>
            </v-menu>

          </v-col>
          <v-spacer />

          <v-col class="pa-0" :cols="roomCols" style="display: flex; align-items: center"  :class="{'pt-2': isMobile}">
            <v-card flat v-for="(room, roomIndex) in availRooms" class="pa-2  ml-2"
              style="color: rgb(var(--v-theme-light)) !important; display: flex; align-items: center;"
              :style="{ 'background-color': room.color_code, color: getContrastColor(room.color_code) }">
              {{ room.name }}
            </v-card>
          </v-col>
          <v-spacer />


          <v-col cols="auto" style="display: flex" v-if="!showRoomCalendar" :class="{'px-2': isMobile}">
            <v-card flat v-for="(loc, locIndex) in locations" class="pa-2 " :class="{'ml-2': !isMobile, 'mr-2': isMobile}"
              style="color: rgb(var(--v-theme-light)) !important; display: flex; align-items: center;"
              :style="{ 'background-color': backgroundColors[locIndex] }">
              {{ $t("constants.locations_" + loc.value) }}
            </v-card>
          </v-col>

          <v-col v-else-if="availUsers" cols="auto" style="display: flex" :class="{'px-2': isMobile}">
            <v-card flat v-for="(user, userIndex) in availUsers" class="pa-2" :class="{'ml-2': !isMobile, 'mr-2': isMobile}"
              style="display: flex; align-items: center;"
              :style="{ 'background-color': user.color_code, color: getContrastColor(user.color_code) }">
              {{ user.full_name }}
            </v-card>
          </v-col cols="auto" style="display: flex">
          
          <v-spacer v-if="isMobile" />
          <v-col cols="auto" style="display: flex" v-if="!profileView">
            <v-tooltip
              :text="showCancelled ? $t('messages.hideCancelledAppointments') : $t('messages.showCancelledAppointments')">
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" class="px-0 rounded-lg"
                  :class="{ outlinedButton: !showCancelled, primaryButton: showCancelled }"
                  @click="showCancelled = !showCancelled"
                  style="min-width: unset !important; width: 46px !important; height: 46px !important" flat>
                  <v-icon :color="!showCancelled ? 'primary' : 'initial'" size="xx-large"
                    class="primaryOutline">mdi-cancel</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <v-container fluid>
      <v-row v-if="profileView">
        <v-spacer v-if=!isMobile />
        <v-col cols="auto">
          <div style="width: 50px; height: 25px; border: 1px solid rgb(var(--v-theme-on-surface), 0.4)" class=""></div>
        </v-col>
        <v-col cols="auto">
          {{ $t("messages.availTimeSlots") }}
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" v-if="columns" class="px-0">
          <TimeTable :availUsers="availUsers" :heightPerSlot="heightPerSlot" :maxDays="maxDays"
            :activeTimeSlot="activeTimeSlot" :duplicationMode="duplicationMode" :timeTable="timeTable"
            :columns="columns" :columnHeaders="columnHeaders" :roomHeaders="roomHeaders"
            :columnLength="timeTable?.dates.length" :startColumn="0" :readonly="readonly" :refresh="refreshTimeTable"
            :showRoomCalendar="showRoomCalendar" :showCancelled="showCancelled" 
            :profileView="profileView" :statusChoices="statusChoices"
            @clickSlot="clickSlot"
            :freeRooms="freeRooms" :availRooms="availRooms" @requestRefresh="fetchTimeTable(0)" />
        </v-col>
      </v-row>
    </v-container>

    <TimeSlotEdit v-if="
      timeTable && !profileView &&
      $checkFunctionPermission(['patch_or_post_timeslot'])" :locations="locations" :statusChoices="statusChoices"
      :refreshDisplay="editTimeSlotDrawer" :slotData="activeTimeSlot"
      :readonly="activeTimeSlot != undefined && activeTimeSlot.id > 0 && !activeTimeSlot.writable"
      :times="this.timeTable.time_units" @loading="setLoading" @close="closeTimeSlotDrawer" @delete="showDeleteTimeSlot"
      @duplicate="showDuplicateTimeSlot" />

    <TimeSlotEdit v-if="
      timeTable &&
      profileView" :locations="locations" :statusChoices="statusChoices"
      :refreshDisplay="editTimeSlotDrawer" :slotData="activeTimeSlot"
      :readonly="true"
      :times="this.timeTable.time_units" @loading="setLoading" @close="closeTimeSlotDrawer" @delete="showDeleteTimeSlot"
      @duplicate="showDuplicateTimeSlot" />

    <DeletionWarningVue style="z-index: 1015 !important" :refreshDisplay="deletionTimeSlotWarningDrawer"
      :type="'appointment'" @delete="confirmDeleteTimeSlot" />
  </div>
  <LoadingScreenVue :loading="loading" />

</template>

<script>
import TimeTable from "./TimeTable.vue";
import TimeSlotEdit from "./TimeSlotEdit.vue";
import TimeTableCard from "./TimeTableCard.vue";

export default {
  name: "timeTableComponent",

  props: {
    "readonly": { default: false },
    "fetchURL": { default: "calendar/calendar/own/" },
    "startDate": undefined,
    "profileView": {default: false}
  },
  emits: ["refetch"],

  data() {
    return {
      timeTable: undefined,
      locations: undefined,
      statusChoices: undefined,

      editTimeSlotDrawer: 0,
      activeTimeSlot: undefined,
      deletionTimeSlotWarningDrawer: 0,

      loading: false,

      columns: undefined,

      activeDate: undefined,

      showCancelled: false,

      showErrors: true,

      refreshTimeTable: 0,

      pickerDate: new Date(),
      datePicker: false,

      freeRooms: undefined,

      availRooms: undefined,
      availUsers: undefined,

      duplicationMode: 0,

      backgroundColors: [
        "rgb(var(--v-theme-primary))",
        "rgb(var(--v-theme-secondary))",
        "rgb(var(--v-theme-success))"
      ],
    };
  },

  components: {
    TimeTable,
    TimeSlotEdit,
    TimeTableCard
  },

  watch: {
    reloadTimeTable() {
      this.timeTable = undefined;

      setTimeout(() => {
        this.timeTable = this.timeTableData;

        this.calculateColumns();
      }, 200);
    },
  },

  computed: {
    profileRoute() {
      if (this.$route.path.startsWith("/profile")) {
        return true
      }

      return false
    },
    dashboardRoute() {
      return this.$route.path == "/backoffice/dashboard"
    },
    heightPerSlot() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 45;
        case "sm":
          return 45;
        case "md":
          return 55;
        case "lg":
          return 58;
        case "xl":
          return 48;
        case "xxl":
          return 48;
        default:
          return 45;
      }
    },
    maxDays() {
      // if (this.showRoomCalendar) {
      //   switch (this.$vuetify.display.name) {
      //     case "xs":
      //       return 1;
      //     case "sm":
      //       return 1;
      //     case "md":
      //       return 2;
      //     case "lg":
      //       return 3;
      //     case "xl":
      //       return 7;
      //     case "xxl":
      //       return 7;
      //     default:
      //       return 7;
      //   }
      // }

      switch (this.$vuetify.display.name) {
        case "xs":
          return 1;
        case "sm":
          return 2;
        case "md":
          return 3;
        case "lg":
          return 7;
        case "xl":
          return 7;
        case "xxl":
          return 7;
        default:
          return 7;
      }
    },
    followUpCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 12;
        case "sm":
          return 12;
        case "md":
          return 12;
        case "lg":
          return 6;
        case "xl":
          return 6;
        case "xxl":
          return 6;
        default:
          return 6;
      }
    },
    roomCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 12;
        case "sm":
          return 12;
        case "md":
          return "auto";
        case "lg":
          return "auto";
        case "xl":
          return "auto";
        case "xxl":
          return "auto";
        default:
          return "auto";
      }
    },
    isMobile() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return true;
        case "sm":
          return true;
        case "md":
          return false;
        case "lg":
          return false;
        case "xl":
          return false;
        case "xxl":
          return false;
        default:
          return false;
      }
    },
    dateString() {
      if (!this.timeTable) {
        return "--";
      }
      let firstIndex;
      let lastIndex;

      firstIndex = 0
      lastIndex = firstIndex + this.maxDays - 1;

      if (lastIndex > this.timeTable.dates.length - 1) {
        lastIndex = this.timeTable.dates.length - 1;
      }

      let temp1 = this.$t(
        "constants.weekday_" + this.$moment(this.timeTable.dates[firstIndex]).day()
      ) +
        " " +
        this.$moment(this.timeTable.dates[firstIndex]).format("DD.MM.YYYY")
      let temp2 = " - " +
        this.$t(
          "constants.weekday_" + this.$moment(this.timeTable.dates[lastIndex]).day()
        ) +
        " " +
        this.$moment(this.timeTable.dates[lastIndex]).format("DD.MM.YYYY")

      if (firstIndex == lastIndex) {
        return temp1
      }

      return (
        temp1 + temp2

      );
    },
    columnHeaders() {
      return undefined;
    },
    roomHeaders() {
      if (!this.availRooms || this.availRooms.length == 0) {
        return undefined;
      }

      if (!this.showRoomCalendar) {
        return undefined
      }

      let result = []

      for (let i = 0; i < this.maxDays; i++) {
        let temp = []
        for (let j = 0; j < this.availRooms.length; j++) {
          temp.push(this.availRooms[j].name)
        }

        temp.push(this.$t("constants.locations_external")
        )

        result.push(temp)
      }

      return result
    },
    showRoomCalendar() {
      return this.$route.path.includes("room")
    }
  },

  methods: {
    getContrastColor(hexColor) {
      // Hex → RGB
      const r = parseInt(hexColor.substr(1, 2), 16) / 255;
      const g = parseInt(hexColor.substr(3, 2), 16) / 255;
      const b = parseInt(hexColor.substr(5, 2), 16) / 255;

      // Gamma-Korrektur
      const toLinear = (c) => {
        return c <= 0.03928
          ? c / 12.92
          : Math.pow((c + 0.055) / 1.055, 2.4);
      };

      const R = toLinear(r);
      const G = toLinear(g);
      const B = toLinear(b);

      // Relative Luminanz
      const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

      // Du kannst hier mit echten Kontrastwerten rechnen,
      // aber für den praktischen Fall reicht ein Threshold:
      return luminance > 0.5
        ? 'rgb(var(--v-theme-on-surface))'   // dunklere Schrift
        : 'rgb(var(--v-theme-light))';       // hellere Schrift
    },
    selectDate() {
      this.activeDate = this.pickerDate

      this.fetchTimeTable(0)
    },
    confirmDeleteTimeSlot() {
      this.loading = true
      this.axios.delete(this.axios.defaults.baseURL + "calendar/timeslots/delete/" + this.activeTimeSlot.id)
        .then(() => {
          this.loading = false
          this.activeTimeSlot = undefined;
          this.editTimeSlotDrawer = 0
          this.deletionTimeSlotWarningDrawer = 0

          this.fetchTimeTable(0)
        })
        .catch((e) => {
          this.loading = false
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },
    showDeleteTimeSlot() {
      this.deletionTimeSlotWarningDrawer++;
    },
    showDuplicateTimeSlot() {
      this.duplicationMode++

      this.editTimeSlotDrawer = 0
    },
    hideDuplicateTimeSlot() {
      this.duplicationMode = 0

      this.activeTimeSlot = undefined
    },
    calculateColumns() {
      if (!this.timeTable) return [];

      let result = [];
      this.startColumn = 0;

      // Determine the day of the week for the first date (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
      const firstDate = this.$moment(this.timeTable.dates[0]);
      const weekday = firstDate.day();

      for (let i = 0; i < this.timeTable.dates.length; i++) {
        let dateString = this.$moment(this.timeTable.dates[i]);
        let temp = {
          col: i,
          value:
            this.$i18n.t(
              "constants.weekday_" + this.$moment(this.timeTable.dates[i]).day()
            ) +
            "<br><p style='color: rgb(var(--v-theme-primary))'>" +
            dateString.format("DD") + "</p>",
        };

        if (this.timeTable.holidays.length > 0) {
          let found = false;

          temp.value +=
            "<br><div style='width:100%' class='overflow-ellipsis text-center'><span style='opacity:0'>.</span>";
          for (let j = 0; j < this.timeTable.holidays.length; j++) {
            if (
              this.timeTable.holidays[j].start_date <=
              dateString.format("YYYY-MM-DD") &&
              this.timeTable.holidays[j].end_date >= dateString.format("YYYY-MM-DD")
            ) {
              if (found) {
                temp.value += ", ";
              }

              temp.value += String(this.timeTable.holidays[j].comment ?? "")
                .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

              found = true;
            }
          }
          temp.value += "</div>";
        }

        result.push(temp);
      }

      this.columns = result;
    },
    fetchTimeTable(daysToAdd, mounted = false) {
      this.loading = true
      let date
      if (mounted && this.startDate) {
        date = new Date(this.startDate); // current active date
      }
      else {
        date = new Date(this.activeDate); // current active date
      }


      date.setDate(date.getDate() + daysToAdd);   // add/subtract days
      this.activeDate = date;

      let activeDateString = this.$moment(this.activeDate).format("YYYY-MM-DD")

      if (!mounted) {
        localStorage.setItem("date", this.activeDate)
      }

      this.axios.post(this.axios.defaults.baseURL + this.fetchURL,
        {
          date: activeDateString,
          days: this.maxDays,
          rooms: this.showRoomCalendar
        }
      )
        .then((resp) => {
          const table = resp.data.time_tables;
          this.timeTable = this.profileView ? {
            ...table,
            time_slots: (table.time_slots || []).filter(slot =>
              slot.own_booking?.is_cancelled !== true &&
              (!slot.display_status || slot.display_status === 'planned') &&
              (slot.public !== true || slot.own_booking?.is_paid === true))
          } : table;
          this.locations = resp.data.locations
          this.statusChoices = resp.data.status_choices
          this.freeRooms = resp.data.free_rooms

          this.calculateColumns()
          this.loading = false

          // ++this.reloadTimeTable
        })
        .catch((e) => {
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
          this.loading = false
        });
    },
    setLoading(value) {
      this.loading = value;
    },
    closeTimeSlotDrawer(value) {
      this.setLoading(false);
      this.editTimeSlotDrawer = 0;

      if (this.duplicationMode) {
        this.activeTimeSlot = value;
      }
      else {
        this.activeTimeSlot = undefined
      }

      if (value) {
        this.fetchTimeTable(0);
      }
    },
    clickSlot(value) {
      console.log("test", value)
      if (value.id && !value.readable) {
        return
      }

      this.activeTimeSlot = value;
      this.editTimeSlotDrawer++;
    },
    fetchRooms() {
      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "calendar/rooms/")
          .then((resp) => {
            this.availRooms = resp.data.results
            resolve()
          })
          .catch((e) => {
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            console.log(e)
            reject()
          })
      })
    },
    fetchUsers() {
      return new Promise((resolve, reject) => {
        let query = "?therapists=1"

        this.axios
          .get(this.axios.defaults.baseURL + "user/users/" + query)
          .then((resp) => {
            this.availUsers = resp.data.results;
            resolve();
          })
          .catch((e) => {
            console.log(e);
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
            resolve();
          });
      });
    },
  },

  mounted() {
    var url = new URL(window.location);
    let date = url.searchParams.get("date");

    let useStorageDate = true
    if (date) {
      this.activeDate = new Date(date)
      useStorageDate = false
    }
    else {
      this.activeDate = new Date()
      if (this.maxDays == 7) {
        //go back to monday of this week
        const day = this.activeDate.getDay()
        const diff = (day === 0 ? -6 : 1 - day) // Montag als Start

        this.activeDate.setDate(this.activeDate.getDate() + diff)
      }
    }

    if(this.profileView){
        this.fetchTimeTable(0)
    }
    else{
      this.fetchUsers().then(() => {
        this.fetchRooms().then(() => {
          this.fetchTimeTable(0, useStorageDate)
        })
      })
    }

  },
};
</script>
